import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Text,
} from "@chakra-ui/react";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Form,
  Formik,
  Field,
  FieldProps,
  FormikProps,
  FieldInputProps,
} from "formik";
import * as Yup from "yup";
import useIsValidEmail from "@/features/auth/hooks/use-is-valid-email";
import debounce from "lodash/debounce";
import { ValidationError } from "yup";
import AppButton from "@/features/line-sheet-sets/app-button";
import useYupHelper from "@/features/ui/form/use-yup-helper";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppSelect from "@/features/line-sheet-sets/app-select";
import { SignUpStep2FormFields } from "@/features/auth/sign-up/sign-up-step2-form";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useGetVerificationCode from "@/features/auth/hooks/useGetVerificationCode";
import useVerifyCode, {
  VerifyCodeResponse,
} from "@/features/auth/hooks/useVerifyCode";
import useMobileValidation from "@/features/auth/hooks/useMobileValidation";

// 테스트 전용 번호 — 이 번호로는 SMS 인증(인증번호 받기/확인)을 건너뛰고 바로 다음 단계로 간다.
// 중복 검사도 건너뛰어 같은 번호로 몇 번이든 다시 가입해볼 수 있다.
// tempToken 은 이후 단계의 진입 조건으로만 쓰이고 가입 API 가 검증하지 않아 임시값으로 채운다.
const TEST_MOBILE_NUMBER = "01012345678";
const TEST_TEMP_TOKEN = "TEST_TEMP_TOKEN";

function isTestMobileNumber(value: string | undefined) {
  return value === TEST_MOBILE_NUMBER;
}

export interface SignUpStep1FormFields {
  // email: string;
  // password: string;
  // passwordConfirmation: string;
  countryCode: string;
  mobileNumber: string;
  mobileVerificationCode: string;
  tempToken: string;
}

interface SignUpStep1FormProps {
  initialValue: SignUpStep1FormFields;
  onSubmit: (formValues: SignUpStep1FormFields) => void;
}

export default function SignUpStep1Form({
  initialValue,
  onSubmit,
}: SignUpStep1FormProps) {
  const { tTitle, tSentence } = useI18nHelper();
  const { error: showError } = useAppToasts();
  const { withMessage } = useYupHelper({
    prefix: "sign_up_form",
    ns: "auth",
  });

  const [isMobileVerificationCodeSent, setIsMobileVerificationCodeSent] =
    useState<boolean>(false);

  const {
    fire: fireGetVerificationCode,
    isLoading: isGetVerificationCodeLoading,
    data: getVerificationCodeData,
    error: getVerificationCodeError,
  } = useGetVerificationCode();

  const {
    fire: fireMobileValidation,
    isLoading: isMobileValidationLoading,
    data: mobileValidationData,
    error: mobileValidationError,
  } = useMobileValidation();

  const {
    fire: fireVerifyCode,
    isLoading: isVerifyCodeLoading,
    data: verifyCodeData,
    error: verifyCodeError,
  } = useVerifyCode();

  useEffect(() => {
    if (verifyCodeError) {
      console.error("verifyCodeError:", verifyCodeError);
      showError(verifyCodeError, {
        id: "SIGN_UP_ERROR",
        i18n: {
          keyPrefix: "sign_up_form.button.verify_code.error",
          ns: "auth",
        },
      });
    }
  }, [verifyCodeError, showError]);

  const [remainingTime, setRemainingTime] = useState<number>(0);
  const [isCountdownActive, setIsCountdownActive] = useState<boolean>(false);
  const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
    useState<boolean>(true);
  const startCountdown = (duration: number) => {
    setRemainingTime(duration);
    setIsCountdownActive(true);
    setIsContinueButtonDisabled(false);
  };
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isCountdownActive && remainingTime > 0) {
      timer = setInterval(() => {
        setRemainingTime((prev) => prev - 1);
      }, 1000);
    } else if (remainingTime <= 0) {
      setIsCountdownActive(false);
      setIsContinueButtonDisabled(true);
    }
    return () => clearInterval(timer);
  }, [isCountdownActive, remainingTime]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  const validateSchema = useMemo(() => {
    return Yup.object({
      mobileNumber: Yup.string()
        .required(withMessage.required)
        .min(10, withMessage.min)
        .test(
          "MobileDuplicate",
          tSentence("mobile_number.duplicate.error.400", { ns: "auth" }),
          async (value) => {
            if (value.length < 10) return false;
            // 테스트 번호는 이미 가입돼 있어도 계속 쓸 수 있어야 한다.
            if (isTestMobileNumber(value)) return true;
            let res = await fireMobileValidation([], {
              mobileNumber: value,
              countryCode: "+82",
            });
            return Promise.resolve(res && res.valid);
          }
        )
        .test(
          "MobileStart010",
          tSentence("mobile_number.start010.error", { ns: "auth" }),
          async (value) => {
            if (!/^010/.test(value)) {
              return false;
            }
            return true;
          }
        ),
      mobileVerificationCode: Yup.string()
        .required(tSentence("auth:mobile_verification_code_required_error"))
        .matches(
          /^\d+$/,
          tSentence("auth:mobile_verification_code_numeric.error")
        )
        .length(4, tSentence("auth:mobile_verification_code_length.error"))
        .test(
          "isCodeExpired",
          tSentence(
            "auth:sign_up_form.field.mobile_verification_code.error.code_expired"
          ),
          () => !(isMobileVerificationCodeSent && remainingTime <= 0)
        ),
      tempToken: Yup.string().required(withMessage.required),
    });
  }, [
    withMessage.required,
    withMessage.min,
    tSentence,
    fireMobileValidation,
    isMobileVerificationCodeSent,
    remainingTime,
  ]);

  useEffect(() => {
    if (getVerificationCodeError) {
      console.error("getVerificationCodeError:", getVerificationCodeError);
    }
    if (getVerificationCodeData) {
      if (getVerificationCodeData.message === "success") {
        setIsMobileVerificationCodeSent(true);
        startCountdown(180);
      }
    }
  }, [getVerificationCodeData, getVerificationCodeError]);

  const [formValues, setFormValues] =
    useState<SignUpStep1FormFields>(initialValue);
  const [onSubmitCalled, setOnSubmitCalled] = useState<boolean>(false);

  useEffect(() => {
    if (formValues && onSubmitCalled) {
      setOnSubmitCalled(false);
      onSubmit(formValues);
    }
  }, [formValues, onSubmitCalled, onSubmit]);

  return (
    <Formik
      initialValues={initialValue}
      validateOnChange={true}
      validationSchema={validateSchema}
      onSubmit={(values, actions) => {
        onSubmit(values);
      }}
    >
      {(props: FormikProps<SignUpStep1FormFields>) => (
        <Form>
          <Box
            display="flex"
            flexDirection="column"
            gap="32px"
            className={"flex flex-col gap-8"}
          >
            <Box display="flex" flexDirection="column" gap="4px">
              <Field name="mobileNumber">
                {({
                  field,
                  form,
                }: {
                  field: FieldInputProps<string>;
                  form: FormikProps<SignUpStep1FormFields>;
                }) => (
                  <FormControl
                    isRequired
                    isInvalid={
                      !!form.errors.mobileNumber && form.touched.mobileNumber
                    }
                  >
                    <FormLabel display="flex" m={"0"}>
                      <Text
                        color="#6F6F6F"
                        fontSize={"12px"}
                        fontWeight={"400"}
                      >
                        {tTitle("sign_up_form.field.mobile_number", {
                          ns: "auth",
                        })}
                      </Text>
                    </FormLabel>

                    <FormControl>
                      <Flex flexDirection={"row"} gap={"8px"}>
                        {/*<AppSelect<string>*/}
                        {/*  value={form.values.countryCode}*/}
                        {/*  name={"KR"}*/}
                        {/*  width={"64px"}*/}
                        {/*  height={"40px"}*/}
                        {/*  options={[*/}
                        {/*    { value: "+82", name: "KR" },*/}
                        {/*    // { value: "+39", name: "IT" },*/}
                        {/*  ]}*/}
                        {/*  isClearable={false}*/}
                        {/*  onSelect={(value) => {*/}
                        {/*    form.setFieldValue("countryCode", value);*/}
                        {/*  }}*/}
                        {/*/>*/}

                        <Input
                          {...field}
                          type={"tel"}
                          inputMode={"numeric"}
                          onChange={(e) => {
                            form.setFieldValue(
                              field.name,
                              e.target.value.replace(/[^0-9]/g, "")
                            );
                          }}
                          fontSize={"12px"}
                          placeholder={tSentence(
                            "auth:sign_up_form.field.mobile_number.placeholder"
                          )}
                        />
                      </Flex>
                    </FormControl>
                    <Flex h="24px" flexDirection="row" alignItems={"center"}>
                      <FormErrorMessage m="0" fontSize={"12px"}>
                        {form.errors.mobileNumber}
                      </FormErrorMessage>
                    </Flex>
                  </FormControl>
                )}
              </Field>
              {!isMobileVerificationCodeSent && (
                <Box textAlign={"center"}>
                  <AppButton
                    isLoading={isGetVerificationCodeLoading}
                    width={"100%"}
                    isDisabled={
                      isGetVerificationCodeLoading ||
                      props.values.mobileNumber.length < 10 ||
                      // 번호 중복 검사가 비동기라, 한 자 전에 나간 요청의 결과가 뒤늦게 도착해
                      // 방금 통과한 값에 옛 에러를 덮어쓸 때가 있다. 그 사이에 누른 클릭은
                      // 비활성 버튼에 떨어져 사라진다(AppButton 이 isDisabled 면 onClick 을 막는다).
                      // 테스트 번호는 애초에 중복 검사를 하지 않으니 이 에러에 걸리지 않게 한다.
                      (!isTestMobileNumber(props.values.mobileNumber) &&
                        !!props.errors.mobileNumber)
                    }
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      // const res = await fireMobileValidation([], {
                      //   mobileNumber: props.values.mobileNumber,
                      //   countryCode: props.values.countryCode,
                      // });
                      //
                      // console.log(res);
                      //
                      // if (!res?.valid) return;

                      // 테스트 번호는 인증번호 발송·확인 없이 곧장 다음 단계로 넘긴다.
                      // onSubmit 을 바로 부른다 — state 로 한 바퀴 돌리면 렌더 한 번을
                      // 더 기다려야 하고, 그동안 다시 눌리면 중복 제출이 된다.
                      if (isTestMobileNumber(props.values.mobileNumber)) {
                        onSubmit({
                          ...formValues,
                          ...props.values,
                          tempToken: TEST_TEMP_TOKEN,
                        });
                        return;
                      }

                      await fireGetVerificationCode(
                        {},
                        {
                          mobileNumber: props.values.mobileNumber,
                        }
                      );
                    }}
                  >
                    {isTestMobileNumber(props.values.mobileNumber)
                      ? tTitle("auth:sign_up_form.button.next.do")
                      : tTitle(
                          "auth:sign_up_form.button.get_verification_code.do"
                        )}
                  </AppButton>
                </Box>
              )}
              {isMobileVerificationCodeSent && (
                <Box textAlign={"center"}>
                  <AppButton
                    isLoading={isGetVerificationCodeLoading}
                    width={"100%"}
                    isDisabled={
                      isGetVerificationCodeLoading ||
                      !!props.errors.mobileNumber ||
                      props.values.mobileNumber.length < 10
                    }
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      //
                      // const { valid } = await fireMobileValidation([], {
                      //   mobileNumber: props.values.mobileNumber,
                      //   countryCode: props.values.countryCode,
                      // });
                      //
                      // if (!valid) return;

                      await fireGetVerificationCode(
                        {},
                        {
                          mobileNumber: props.values.mobileNumber,
                        }
                      );
                    }}
                  >
                    {tTitle("auth:sign_up_form.button.resend_code.do", {
                      remainingTime: formatTime(remainingTime),
                    })}
                  </AppButton>
                </Box>
              )}
            </Box>
            <Box display={"flex"} flexDirection={"column"}>
              {isMobileVerificationCodeSent && (
                <Field name="mobileVerificationCode">
                  {({
                    field,
                    form,
                  }: {
                    field: FieldInputProps<string>;
                    form: FormikProps<SignUpStep1FormFields>;
                  }) => (
                    <FormControl
                      isRequired
                      isInvalid={
                        !!form.errors.mobileVerificationCode &&
                        form.touched.mobileVerificationCode
                      }
                    >
                      <FormLabel display="flex" m={"0"}>
                        <Text
                          color="#6F6F6F"
                          fontSize={"12px"}
                          fontWeight={"400"}
                        >
                          {tTitle(
                            "auth:sign_up_form.field.mobile_verification_code.label"
                          )}
                        </Text>
                      </FormLabel>

                      <FormControl>
                        <Flex flexDirection={"row"} gap={"8px"}>
                          <Input
                            {...field}
                            type={"text"}
                            inputMode={"numeric"}
                            placeholder={tSentence(
                              "auth:sign_up_form.field.mobile_verification_code.placeholder"
                            )}
                            fontSize={"12px"}
                          />
                        </Flex>
                      </FormControl>
                      <Flex h="24px" flexDirection="row" alignItems={"center"}>
                        <FormErrorMessage
                          m="0"
                          fontSize={"12px"}
                          whiteSpace="nowrap"
                        >
                          {form.errors.mobileVerificationCode}
                        </FormErrorMessage>
                      </Flex>
                    </FormControl>
                  )}
                </Field>
              )}
              {isMobileVerificationCodeSent && (
                <Box textAlign={"center"}>
                  <AppButton
                    isLoading={isVerifyCodeLoading}
                    width={"100%"}
                    isDisabled={
                      isContinueButtonDisabled ||
                      !!props.errors.mobileVerificationCode
                    }
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const promise: VerifyCodeResponse = await fireVerifyCode(
                        {},
                        {
                          mobileNumber: props.values.mobileNumber,
                          verificationCode: props.values.mobileVerificationCode,
                        }
                      );
                      setFormValues({
                        ...formValues,
                        ...props.values,
                        tempToken: promise.tempToken,
                      });
                      setOnSubmitCalled(true);
                    }}
                  >
                    {tTitle("auth:sign_up_form.button.verify_code.do")}
                  </AppButton>
                </Box>
              )}
            </Box>
          </Box>
        </Form>
      )}
    </Formik>
  );
}
